import React from 'react';
import { Mail, Users, TrendingUp, Eye } from 'lucide-react';
import FormModal from './FormModal';

interface EmailCampaign {
  id: number;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  template_id: number;
  trigger_id?: number;
  recipient_count: number;
  sent_count: number;
  open_rate: number;
  click_rate: number;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
}

interface EmailCampaignPreviewModalProps {
  campaign: EmailCampaign;
  onClose: () => void;
}

export const EmailCampaignPreviewModal: React.FC<EmailCampaignPreviewModalProps> = ({
  campaign,
  onClose,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-slate-100 text-slate-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'sending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <FormModal
      title="Campaign Preview"
      subtitle="Review campaign details and performance"
      icon={<Eye className="text-white" size={24} />}
      onSubmit={() => {}} // No submit action for preview
      onCancel={onClose}
      cancelText="Close"
      showSubmitButton={false}
      size="large"
    >

        <div className="space-y-6">
          {/* Campaign Header */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-slate-900">{campaign.name}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </span>
            </div>
            <p className="text-slate-700 mb-2">
              <strong>Subject:</strong> {campaign.subject}
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span>Created: {new Date(campaign.created_at).toLocaleDateString()}</span>
              {campaign.scheduled_at && (
                <span>Scheduled: {new Date(campaign.scheduled_at).toLocaleDateString()}</span>
              )}
              {campaign.sent_at && (
                <span>Sent: {new Date(campaign.sent_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-slate-700">Recipients</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{campaign.recipient_count.toLocaleString()}</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="text-green-600" size={16} />
                <span className="text-sm font-medium text-slate-700">Sent</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{campaign.sent_count.toLocaleString()}</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="text-purple-600" size={16} />
                <span className="text-sm font-medium text-slate-700">Open Rate</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{campaign.open_rate.toFixed(1)}%</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-slate-700">Click Rate</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{campaign.click_rate.toFixed(1)}%</div>
            </div>
          </div>

          {/* Campaign Details */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Campaign Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-600">Template ID:</span>
                <span className="ml-2 font-mono text-slate-900">{campaign.template_id}</span>
              </div>
              {campaign.trigger_id && (
                <div>
                  <span className="text-slate-600">Trigger ID:</span>
                  <span className="ml-2 font-mono text-slate-900">{campaign.trigger_id}</span>
                </div>
              )}
              <div>
                <span className="text-slate-600">Last Updated:</span>
                <span className="ml-2 text-slate-900">{new Date(campaign.updated_at).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-slate-600">Campaign ID:</span>
                <span className="ml-2 font-mono text-slate-900">#{campaign.id}</span>
              </div>
            </div>
          </div>

          {/* Progress Bar for Sent vs Total */}
          {campaign.recipient_count > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-900">Delivery Progress</span>
                <span className="text-sm text-slate-600">
                  {campaign.sent_count} of {campaign.recipient_count} sent
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(campaign.sent_count / campaign.recipient_count) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Close
          </button>
        </div>
    </FormModal>
  );
};